/**
 * The console's enum vocabulary — one label per value, in one place.
 *
 * Before this there were two divergent copies: the manual-record form called
 * `engagement_hours_submission` "Engagement hours submission" while the queue
 * table called it "Engagement hours", and `response_referral` was
 * "Written-response referral" in one and "Response referral" in the other. That
 * is a real problem on an operations tool once the queue gains filters — an
 * operator picks a filter by one name and reads a different name in the row it
 * returns, and has to decide whether those are the same thing.
 *
 * The precise wording wins over the terse wording. The queue's "For" column
 * already stacks the label over a description, so it has the room, and on a
 * money screen naming the thing exactly is worth more than saving a word.
 *
 * The option arrays are ordered, not derived from the label maps: consultation
 * leads because it is what the manual rail is for today (the payout run Finance
 * does by hand), and the ordering says so.
 */
import {
  ExpertPaymentKind,
  ExpertPaymentSourceType,
  ExpertPaymentStatus,
} from '@/__generated__/graphql';

export interface EnumOption<T extends string> {
  value: T;
  label: string;
}

export const SOURCE_TYPE_OPTIONS: EnumOption<ExpertPaymentSourceType>[] = [
  { value: ExpertPaymentSourceType.Consultation, label: 'Consultation' },
  { value: ExpertPaymentSourceType.Response, label: 'Written response' },
  { value: ExpertPaymentSourceType.ResponseReferral, label: 'Written-response referral' },
  {
    value: ExpertPaymentSourceType.EngagementHoursSubmission,
    label: 'Engagement hours submission',
  },
];

export const KIND_OPTIONS: EnumOption<ExpertPaymentKind>[] = [
  { value: ExpertPaymentKind.WorkFee, label: 'Work fee' },
  { value: ExpertPaymentKind.ReferralBonus, label: 'Referral bonus' },
  { value: ExpertPaymentKind.Goodwill, label: 'Goodwill' },
];

export const STATUS_TABS: EnumOption<ExpertPaymentStatus>[] = [
  { value: ExpertPaymentStatus.Pending, label: 'Awaiting release' },
  { value: ExpertPaymentStatus.Processing, label: 'Settling' },
  { value: ExpertPaymentStatus.Failed, label: 'Failed' },
  { value: ExpertPaymentStatus.Paid, label: 'Paid' },
  { value: ExpertPaymentStatus.Canceled, label: 'Canceled' },
];

/**
 * Lookup maps for rendering a value the server sent.
 *
 * Keyed by the raw string rather than the generated enum because that is what
 * arrives on the wire, and an unrecognised value must render as itself rather
 * than as blank — a row whose kind the client does not know is still a row
 * carrying money, and hiding what it is would be worse than showing a raw
 * `work_fee`.
 */
function labelsOf<T extends string>(options: EnumOption<T>[]): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

export const SOURCE_LABEL = labelsOf(SOURCE_TYPE_OPTIONS);
export const KIND_LABEL = labelsOf(KIND_OPTIONS);

export const RAIL_LABEL: Record<string, string> = {
  stripe_transfer: 'Stripe transfer',
  manual: 'Manual',
};
